import { useState } from "react";
import type { LiveItem } from "@/mocks/lives";
import StatusBadge from "@/components/base/StatusBadge";

type PlayerState = "idle" | "loading" | "error" | "playing";

interface PlayerAreaProps {
  item: LiveItem;
}

export default function PlayerArea({ item }: PlayerAreaProps) {
  const [state, setState] = useState<PlayerState>("idle");

  const startPlay = () => {
    setState("loading");
    window.setTimeout(() => {
      setState("error");
    }, 1800);
  };

  const retry = () => {
    setState("loading");
    window.setTimeout(() => {
      setState("playing");
    }, 1800);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-background-200/60 bg-background-100">
      <div className="relative aspect-video w-full overflow-hidden bg-background-50">
        <img
          src={item.cover}
          alt={`${item.streamer} 直播 ${item.title}`}
          title={`${item.title} - 360直播`}
          className={`h-full w-full object-cover object-top transition-all duration-500 ${
            state === "playing" ? "opacity-60 blur-[2px]" : "opacity-100"
          }`}
        />

        {state !== "playing" && (
          <div className="absolute inset-0 bg-gradient-to-t from-background-50/90 via-background-50/30 to-background-50/50" />
        )}

        {state === "idle" && (
          <button
            type="button"
            onClick={startPlay}
            aria-label="播放直播"
            className="absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-4"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform hover:scale-110 md:h-20 md:w-20">
              <i className="ri-play-fill text-3xl md:text-4xl" />
            </span>
            <span className="text-sm font-medium text-foreground-800">点击播放直播</span>
          </button>
        )}

        {state === "loading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            <span className="h-12 w-12 animate-spin rounded-full border-2 border-primary-500/30 border-t-primary-400" />
            <span className="text-sm font-medium text-foreground-700">正在加载直播流…</span>
            <span className="text-xs text-foreground-500">正在为你匹配最佳清晰度线路</span>
          </div>
        )}

        {state === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-500/15 text-accent-400">
              <i className="ri-error-warning-line text-2xl" />
            </span>
            <span className="text-sm font-semibold text-foreground-900">直播源连接失败</span>
            <span className="max-w-sm text-xs text-foreground-500">
              当前直播线路暂时无法连接，请稍后重试或切换清晰度。（本播放器为前端演示，真实播放接口待接入）
            </span>
            <button
              type="button"
              onClick={retry}
              className="mt-1 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line" />
              重新加载
            </button>
          </div>
        )}

        {state === "playing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <span className="flex items-center gap-2 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-bold text-background-50">
              <span className="h-1.5 w-1.5 rounded-full bg-background-50 animate-live-pulse" />
              演示播放中
            </span>
            <span className="text-xs text-foreground-700">
              播放器为前端演示，真实直播流接入后即可观看
            </span>
          </div>
        )}

        <div className="absolute left-4 top-4 flex items-center gap-2">
          <StatusBadge status={item.status} />
          <span className="rounded-md bg-background-50/70 px-2 py-1 text-[11px] font-medium text-foreground-900 backdrop-blur-sm">
            {item.resolution}
          </span>
        </div>
      </div>

      {/* Player control bar (demo) */}
      <div className="flex flex-wrap items-center gap-3 border-t border-background-200/60 px-4 py-3">
        <button
          type="button"
          onClick={state === "idle" ? startPlay : undefined}
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md bg-primary-500/15 text-primary-300 transition-colors hover:bg-primary-500 hover:text-background-50"
          aria-label="播放"
        >
          <i className="ri-play-fill" />
        </button>
        <div className="h-1 flex-1 rounded-full bg-background-200">
          <div className="h-1 w-1/3 rounded-full bg-primary-500" />
        </div>
        <span className="text-xs text-foreground-500">00:00 / 直播中</span>
        <div className="ml-auto flex items-center gap-2">
          {["蓝光", "超清", "高清"].map((quality) => (
            <button
              key={quality}
              type="button"
              className={`whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                quality === "蓝光"
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              {quality}
            </button>
          ))}
          <span className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-600">
            <i className="ri-fullscreen-line" />
          </span>
        </div>
      </div>
    </div>
  );
}